import { projects } from "@folio-2026/content"
import Experience from "@folio-2026/experience"
import Section from "@folio-2026/section"
import { EVENTS, MQ, lerp, scaleUV } from "@folio-2026/shared"
import TextFollowingMouse, {
  type MSDFFont,
} from "@folio-2026/shared/textfollowingmouse"
import type { Text } from "@folio-2026/text"
import gsap from "gsap"
import { GLTF } from "three/examples/jsm/Addons.js"
import BloomNode, { bloom } from "three/examples/jsm/tsl/display/BloomNode.js"
import { gaussianBlur } from "three/examples/jsm/tsl/display/GaussianBlurNode.js"
import { outline } from "three/examples/jsm/tsl/display/OutlineNode.js"
import {
  abs,
  clamp,
  emissive,
  float,
  length,
  mix,
  mrt,
  oscSine,
  output,
  pass,
  sin,
  time,
  uniform,
  uv,
  vec2,
} from "three/tsl"
import * as THREE from "three/webgpu"
import { Texture } from "three/webgpu"

import WorksSectionCamera from "./Camera"
import Cinema from "./Cinema"
import { Glow } from "./Glow"
import Ki from "./Ki"
import Man from "./Man"
import Particles from "./Particles"
import ProjectList from "./ProjectList"
import Room from "./Room"
import Strikes from "./Strikes"
import Wind from "./Wind"
import { cameraMovements } from "./const"
import sources from "./sources"

export default class WorksSection extends Section {
  name = "works"

  private static readonly REVEAL_STAGGER_DURATION = 0.12

  experience: Experience

  cinema!: Cinema
  projectList!: ProjectList
  particles!: Particles
  wind!: Wind
  fog!: THREE.Fog
  strikes!: Strikes[]
  ki!: Ki
  glow!: Glow
  postProcessing!: THREE.PostProcessing

  room!: Room
  man!: Man

  closeText!: TextFollowingMouse
  private hasSetCloseTextPosition = false

  /** Cached at init: texts with name "works". */
  private worksTextsCache: Text[] = []
  /** Cached at init: works texts with data-text="intro". */
  private worksIntroTextsCache: Text[] = []

  /** Tracks intro text animation state to avoid redundant reveal/leave. */
  private introTextState: "revealed" | "revealing" | "leaving" | "left" = "left"
  private introTextRevealedDelay?: ReturnType<typeof gsap.delayedCall>
  private introTextLeftDelay?: ReturnType<typeof gsap.delayedCall>

  isMobile = uniform(false)
  blurDirectionNode = uniform(0)
  distortionProgress = uniform(0)
  distortionScale = uniform(0.5)
  hourglassIntensity = uniform(0.0)
  hourglassCenter = uniform(0.5)
  hourglassWidth = uniform(0.3)

  // Outline pass uniforms (tweakable via debug)
  edgeStrength = uniform(0.1) // 3
  edgeGlow = uniform(1.24) // 1.24
  edgeThickness = uniform(1.0) // 8
  pulsePeriod = uniform(3) // 0.5
  visibleEdgeColor = uniform(new THREE.Color(0x6500f5)) // 006cf5
  outlineDownSampleRatio = 2 // 4
  outlinePass!: ReturnType<typeof outline>

  declare camera: WorksSectionCamera

  constructor() {
    super({
      name: "works",
      sources,
      folderName: "🧑‍🏭 Works scene",
      expanded: false,
    })

    this.experience = Experience.getInstance()

    this.setDebug()

    this.setIsMobileUniform()

    this.closeText = new TextFollowingMouse({
      scene: this.textScene.scene,
      atlas: this.experience.globalResources
        .atlasUbuntuSansMonoRegular as THREE.Texture,
      font: this.experience.globalResources.bmfontUbuntuMonoRegular as MSDFFont,
      text: "[ close ]",
      startHidden: true,
      onClick: () => {
        this.experience.navigate("/")
      },
      noiseTexture: this.experience.globalResources.perlin as THREE.Texture,
    })

    /**
     * PROJECT SLIDER
     * This contains ProjectLeafs
     * This needs to be done on the constructor
     */
    this.camera = new WorksSectionCamera({ scene: this.scene })
    this.setupCamera()
  }

  init() {
    super.init()

    this.setup()
    this.setPostProcessing()

    this.worksTextsCache = this.textScene.texts.filter(
      (text) => text.name === "works",
    )
    this.worksIntroTextsCache = this.worksTextsCache.filter(
      (text) => text.element.getAttribute("data-text") === "intro",
    )

    this.createAudio("works-pre-aura")
    this.createEffect("screaming-1")
    this.createEffect("screaming-2")
    this.createEffect("aura-explosion-loop")
    this.createEffect("aura-explosion")

    this.experience.trigger(EVENTS.Works.SectionReady)
  }

  private hasInitializedProjectOverlayTexts = false

  private initProjectOverlayTextsOnce() {
    if (this.hasInitializedProjectOverlayTexts) return

    if (!this.textScene.texts.some((text) => text.name === "project")) {
      this.textScene.createTexts("project")
    }

    if (
      !this.textScene.smallButtons.some((button) => button.name === "project")
    ) {
      this.textScene.createSmallButtons("project")
    }

    this.hasInitializedProjectOverlayTexts = true
  }

  eventListeners() {
    super.eventListeners()

    this.experience.on(EVENTS.Scroll.Section.StartUpdate, (e: unknown) => {
      const { name } = e as { name: string }
      if (name !== this.name) {
        return
      }

      this.worksIntroTextsCache.forEach((text) => {
        text.revertReveal()
      })
    })

    this.experience.on(
      EVENTS.Works.BeforeEnter,
      this.beforeEnterAnimation.bind(this),
    )

    this.experience.on(EVENTS.Works.Entering, (e: unknown) => {
      const { progress, direction } = e as {
        progress: number
        direction: string
      }

      this.toggleIntroTexts(direction)

      if (!this.audio) {
        return
      }

      const volumeLevel = progress * this.experience.volume

      this.setVolume(volumeLevel)
      this.effects?.get("screaming-1")?.setVolume(volumeLevel)
      this.effects?.get("screaming-2")?.setVolume(volumeLevel)
      this.effects?.get("aura-explosion-loop")?.setVolume(volumeLevel)
      this.effects?.get("aura-explosion")?.setVolume(volumeLevel)
    })

    this.experience.on(EVENTS.Works.LeaveIntroTexts, () => {
      // console.log("LEAVING INTRO TEXTs (EVENTS.Works.LeaveIntroTexts)")
      this.leaveIntroTexts()
    })

    this.experience.on(EVENTS.Works.LeaveWorks, () => {
      const currentPathname = window.location.pathname
      const currentProject = projects.find((p) => p.url === currentPathname)
      if (currentProject) {
        return
      }
      // console.log("REVEALING INTRO TEXTs (LEAVE WORKS)")
      this.revealIntroTexts()
    })

    this.experience.on(EVENTS.Audio.SetVolume, (e: unknown) => {
      const { volume } = e as { volume: number }

      if (this.experience.scrollInfo.currentSection?.name === this.name) {
        this.setVolume(volume)
        this.effects?.get("screaming-1")?.setVolume(volume)
        this.effects?.get("screaming-2")?.setVolume(volume)
        this.effects?.get("aura-explosion-loop")?.setVolume(volume)
        this.effects?.get("aura-explosion")?.setVolume(volume)
      }
    })

    this.experience.on(
      EVENTS.Works.EnteringBack,
      this.enterScrollAnimationBack.bind(this),
    )

    this.experience.on(
      EVENTS.Works.Entered,
      this.enterAnimationFinished.bind(this),
    )

    this.experience.on(
      EVENTS.Project.Entered,
      this.projectEnteredHandler.bind(this),
    )

    this.experience.on(
      EVENTS.Project.Entered,
      this.initProjectOverlayTextsOnce.bind(this),
    )

    this.experience.on(EVENTS.Project.Entered, () => {
      if (this.experience.pointer.primaryInput !== "mouse") {
        this.closeText.leave()
        this.hasSetCloseTextPosition = false
        return
      }

      this.hasSetCloseTextPosition = false
      this.closeText.reveal()
    })

    this.experience.on(EVENTS.Project.Exiting, () => {
      this.closeText.leave()
      this.hasSetCloseTextPosition = false
    })

    this.experience.on(EVENTS.Project.VisitWebsiteCTA.Hover, () => {
      if (this.closeText?.textMesh?.visible) {
        this.closeText.leave({ duration: 0.2 })
      }
    })

    this.experience.on(EVENTS.Project.VisitWebsiteCTA.Out, () => {
      if (
        this.experience.pointer.primaryInput === "mouse" &&
        this.experience.scrollInfo.currentSection?.name === "works"
      ) {
        // Check if we're on a project page
        const pathname = window.location.pathname
        const currentProject = projects.find((p) => p.url === pathname)
        if (currentProject) {
          this.hasSetCloseTextPosition = false
          this.closeText.reveal({ duration: 0.3 })
        }
      }
    })

    this.experience.on(EVENTS.Project.Exiting, () => {
      gsap.to(this.blurDirectionNode, {
        value: 0,
        duration: 0.5,
        ease: "power2.out",
      })
    })

    this.experience.on(EVENTS.Works.Exiting, (e: unknown) => {
      const { progress } = e as { progress: number }
      const volumeLevel = (1 - progress) * this.experience.volume

      this.setVolume(volumeLevel)
      this.effects?.get("screaming-1")?.setVolume(volumeLevel)
      this.effects?.get("screaming-2")?.setVolume(volumeLevel)
      this.effects?.get("aura-explosion-loop")?.setVolume(volumeLevel)
      this.effects?.get("aura-explosion")?.setVolume(volumeLevel)
    })

    this.experience.on(
      EVENTS.Project.Exiting,
      this.projectExitingHandler.bind(this),
    )
  }

  positionTexts() {
    this.worksTextsCache.forEach((text) => {
      const bounds = text.element.getBoundingClientRect()
      text.y = bounds.top

      const positionY = -text.y + this.sizes.height / 2 - bounds.height

      const positionX = bounds.left - this.sizes.width / 2

      text.mesh.position.y = positionY
      text.mesh.position.x = positionX
    })
  }

  toggleIntroTexts(direction: string) {
    const directionNum = Number(direction)
    const isPositive = directionNum > 0

    if (isPositive) {
      if (
        this.introTextState === "revealed" ||
        this.introTextState === "revealing"
      ) {
        return
      }
      this.introTextLeftDelay?.kill()
      this.introTextLeftDelay = undefined
      this.introTextState = "revealing"
      this.positionTexts()
      // console.log("REVEALING INTRO TEXTs (TOGGLE)")
      this.revealIntroTexts()
      const count = this.worksIntroTextsCache.length
      const totalRevealTime =
        (count > 0 ? (count - 1) * WorksSection.REVEAL_STAGGER_DURATION : 0) +
        0.6 +
        1 +
        0.2
      this.introTextRevealedDelay?.kill()
      this.introTextRevealedDelay = gsap.delayedCall(totalRevealTime, () => {
        this.introTextState = "revealed"
        this.introTextRevealedDelay = undefined
      })
    } else {
      if (this.introTextState === "leaving" || this.introTextState === "left") {
        return
      }
      this.introTextRevealedDelay?.kill()
      this.introTextRevealedDelay = undefined
      this.introTextState = "leaving"
      // console.log("LEAVING INTRO TEXTs (TOGGLE)")
      this.leaveIntroTexts()
      this.introTextLeftDelay?.kill()
      this.introTextLeftDelay = gsap.delayedCall(1.5, () => {
        this.introTextState = "left"
        this.introTextLeftDelay = undefined
      })
    }
  }

  revealIntroTexts() {
    // Reset to hidden so re-entering the section (e.g. after scrolling away
    // mid-reveal) always starts from a consistent state instead of briefly
    // showing the previous progress then jumping to reveal.
    // this.worksIntroTextsCache.forEach((text) => text.resetToRevealStart())

    const revealables = this.worksIntroTextsCache.map((text) => ({
      reveal: () => text.reveal(),
      top: text.bounds.top,
    }))
    revealables.sort((a, b) => a.top - b.top)
    revealables.forEach((item, index) => {
      gsap.delayedCall(
        index * WorksSection.REVEAL_STAGGER_DURATION,
        item.reveal,
      )
    })
  }

  leaveIntroTexts() {
    this.worksIntroTextsCache.forEach((text) => {
      text.leave()
    })
  }

  setIsMobileUniform() {
    const width = this.sizes.width
    this.isMobile.value = width < MQ.lg
  }

  setupCamera() {
    this.camera.camera.name = "works-camera"

    const camMov = cameraMovements.get("initial")

    this.camera.camera.position.set(
      camMov.positionX,
      camMov.positionY,
      camMov.positionZ,
    )
    this.camera.camera.lookAt(camMov.lookAtX, camMov.lookAtY, camMov.lookAtZ)
  }

  setup() {
    // Add point light
    const pointLight = new THREE.PointLight(0xffffff, 6)
    pointLight.position.set(1, 1, 1)
    this.scene.add(pointLight)

    this.projectList = new ProjectList({
      textScene: this.textScene,
      debugFolder: this.debugFolder,
      resources: {
        ...this.experience.globalResources,
        ...this.resources.items,
      } as {
        [key: string]: Texture
      },
    })

    /**
     * ROOM
     */
    this.room = new Room({
      scene: this.scene,
      resources: this.experience.globalResources as { [key: string]: GLTF },
      debugFolder: this.debugFolder,
    })

    /**
     * CINEMA
     */
    this.cinema = new Cinema({
      scene: this.scene,
      debugFolder: this.debugFolder,
    })

    /**
     * MAN
     */
    this.man = new Man({
      scene: this.scene,
      debugFolder: this.debugFolder,
      resources: {
        ...this.experience.globalResources,
        ...this.resources.items,
      },
      pointLight: this.room.pointLight,
    })

    /**
     * KI
     */
    // this.ki = new Ki({
    //   scene: this.scene,
    //   resources: this.experience.globalResources as {
    //     [key: string]: THREE.Texture
    //   },
    //   debugFolder: this.debugFolder,
    // })

    /**
     * Strikes
     */
    const strikesCount = 3
    this.strikes = []
    for (let i = 0; i < strikesCount; i++) {
      this.strikes.push(
        new Strikes({
          scene: this.scene,
        }),
      )
    }

    /**
     * WIND
     */
    // this.wind = new Wind({
    //   scene: this.scene,
    //   resources: this.experience.globalResources as {
    //     [key: string]: GLTF | THREE.Texture
    //   },
    //   debugFolder: this.debugFolder,
    // })

    /**
     * PARTICLES
     */
    this.particles = new Particles({
      scene: this.scene,
      debugFolder: this.debugFolder,
    })
    this.particles.settings.animationType.value = 1

    // Fog
    this.fog = new THREE.Fog(0x182e45, 5, 30)
    this.scene.fog = this.fog

    this.setFogDebug()
  }

  setPostProcessing(): void {
    super.setPostProcessing()

    // const distortedUV = spherizeUV(uv(), this.distortionScale)
    const uvs = uv()
    const dist = length(uvs.sub(0.5))
    // Max distance from center to corner in UV space is ~0.707
    const maxDist = 0.707
    // Wave frequency - controls the width/sharpness of the wave
    const waveFrequency = 20.0
    // Calculate wave position:
    // - progress 0: wavePosition = 0 (wave at center, but no distortion due to fade)
    // - progress 0.5: wavePosition = maxDist (wave at edges, full distortion)
    // - progress 1: wavePosition = maxDist * 2 (wave past edges, no distortion)
    const wavePosition = this.distortionProgress.mul(maxDist).mul(2)
    // Create single wave that moves outward: sin wave based on distance from wave position
    const wave = sin(dist.sub(wavePosition).mul(waveFrequency))
    // Normalize wave from [-1, 1] to [0, 1]
    const normalizedWave = wave.add(1).mul(0.5)
    // Create fade factor: 0 at progress 0, 1 at progress 0.5, 0 at progress 1
    // This ensures no distortion at start and end, full distortion when wave is at edges
    const fadeFactor = float(1)
      .sub(abs(this.distortionProgress.sub(0.5)).mul(2))
      .max(0)
    // Apply fade and distortion scale
    const waveFactor = normalizedWave.mul(fadeFactor).mul(this.distortionScale)
    // Apply distortion: single wave propagating from center outward
    const distortedUV = scaleUV(
      uvs,
      float(1).sub(vec2(waveFactor)), // Single wave of distortion moving outward
    )

    this.postProcessing = new THREE.PostProcessing(this.renderer.instance)
    /**
     * Whether the default output tone mapping and color
     * space transformation should be enabled or not.
     *
     * It is enabled by default by it must be disabled when
     * effects must be executed after tone mapping and color
     * space conversion. A typical example is FXAA which
     * requires sRGB input.
     *
     * When set to `false`, the app must control the output
     * transformation with `RenderOutputNode`.
     * */
    // this.postProcessing.outputColorTransform = false

    const scenePass = pass(this.scene, this.camera.camera)

    scenePass.setMRT(
      mrt({
        output,
        emissive,
      }),
    )

    const outputPass = scenePass.getTextureNode()
    const distortedOutputPass = outputPass.sample(distortedUV)
    const emissivePass = scenePass.getTextureNode("emissive")

    // outline pass
    this.outlinePass = outline(this.scene, this.camera.camera, {
      selectedObjects: [this.man.model],
      edgeGlow: this.edgeGlow,
      edgeThickness: this.edgeThickness,
      downSampleRatio: this.outlineDownSampleRatio,
    })

    const { visibleEdge } = this.outlinePass

    const period = time.div(this.pulsePeriod).mul(2)
    const osc = oscSine(period).mul(0.5).add(0.5) // osc [ 0.5, 1.0 ]

    const outlineColor = visibleEdge
      .mul(this.visibleEdgeColor)
      .mul(this.edgeStrength)
    const outlinePulse = outlineColor.mul(osc)

    //
    // Bloom
    const bloomPass = bloom(emissivePass, 2.5, 0.5, 0.022)

    const outputColor = distortedOutputPass.add(outlinePulse).add(bloomPass)

    const projectListOutput = this.projectList.postProcessing.outputNode

    // Desktop path: blur and darken first, then blend

    const blurred = gaussianBlur(outputColor, this.blurDirectionNode)
    const darkeningFactor = clamp(
      this.blurDirectionNode.mul(0.5).oneMinus(),
      0.15,
      1.0,
    )
    const darkened = blurred.mul(darkeningFactor).add(projectListOutput)

    // const finalOutput = darkened.add(this.textScene.postProcessing.outputNode)
    const finalOutput = mix(
      darkened,
      projectListOutput.rgb,
      projectListOutput.a,
    )

    //
    // Text scene
    // this.postProcessing.outputNode = blendColor(
    //   finalOutput,
    //   this.textScene.postProcessing.outputNode,
    // )
    this.postProcessing.outputNode = finalOutput.add(
      this.textScene.postProcessing.outputNode,
    )

    this.setPPDebug({ bloomPass, outlinePass: this.outlinePass })
  }

  projectEnteredHandler() {
    this.leaveIntroTexts()

    gsap.to(this.blurDirectionNode, {
      value: 8,
      duration: 0.8,
      ease: "power2.inOut",
    })
  }

  projectExitingHandler() {
    gsap.to(this.blurDirectionNode, {
      value: 0,
      duration: 0.5,
      ease: "power3.inOut",
    })
  }

  /**
   * this gets fired before the enter animation, when the section is about to enter (onEnter event of the ScrollTrigger)
   */
  beforeEnterAnimation() {
    this.resetOutlinePass()

    this.playAudio()
    this.playEffect("screaming-1")

    //
    if (this.particles) {
      this.blurDirectionNode.value = 0
      this.particles.settings.animationType.value = 0
      this.man.outerParticles.settings.speed.value = 0.15
    }
  }

  playEffect(effect: string) {
    const effectInstance = this.effects?.get(effect)
    if (effectInstance && !effectInstance.isPlaying) {
      effectInstance.play()
    }
  }

  /**
   * Called when the snapping scroll starts going back
   */
  enterScrollAnimationBack() {
    // console.log("LEAVING INTRO TEXTs (ENTER SCROLL ANIMATION BACK)")
    this.leaveIntroTexts()
  }

  animateOutlinePass() {
    /*
      edgeStrength = uniform(0.1) // 3
  edgeGlow = uniform(1.24) // 1.24
  edgeThickness = uniform(1.0) // 8
  pulsePeriod = uniform(3) // 0.5
  visibleEdgeColor = uniform(new THREE.Color(0x6500f5)) // 006cf5
  */
    gsap.to(this.edgeStrength, {
      value: 3,
      duration: 1,
      ease: "power2.inOut",
      onComplete: () => {
        this.outlinePass.downSampleRatio = 4
      },
    })
    gsap.to(this.edgeThickness, {
      value: 8,
      duration: 1,
      ease: "power2.inOut",
    })
    gsap.to(this.pulsePeriod, {
      value: 0.5,
      duration: 1,
      ease: "power2.inOut",
    })
  }

  resetOutlinePass() {
    this.edgeStrength.value = 0.1
    this.edgeThickness.value = 1.0
    this.pulsePeriod.value = 3
    this.visibleEdgeColor.value = new THREE.Color(0x6500f5)
    this.outlinePass.downSampleRatio = 2
  }

  /**
   * This is called when the enter animation is finished
   */
  enterAnimationFinished() {
    // Man Aura
    this.animateOutlinePass()

    // Man Screaming
    this.playEffect("screaming-2")

    // Camera shaking
    const auraExplosionEffect = this.effects?.get("aura-explosion")
    const auraExplosionLoopEffect = this.effects?.get("aura-explosion-loop")

    if (this.particles.settings.animationType.value === 1) {
      return
    }

    // Blur effect
    if (window.location.pathname === "/") {
      gsap.to(this.blurDirectionNode, {
        value: 1,
        duration: 2,
        ease: "power3.inOut",
      })
    }

    if (auraExplosionEffect) {
      this.playEffect("aura-explosion")

      // Set up listener for when aura-explosion ends
      const setupAuraExplosionEndListener = () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const auraSource = (auraExplosionEffect as any).source as
          | AudioBufferSourceNode
          | undefined

        if (auraSource) {
          const handleAuraExplosionEnd = () => {
            auraSource.removeEventListener("ended", handleAuraExplosionEnd)

            if (auraExplosionLoopEffect) {
              auraExplosionLoopEffect.setLoop(true)
              this.playEffect("aura-explosion-loop")
            }
          }

          auraSource.addEventListener("ended", handleAuraExplosionEnd)
        } else {
          // Fallback: use buffer duration
          const buffer = auraExplosionEffect.buffer
          if (buffer) {
            setTimeout(() => {
              if (auraExplosionLoopEffect) {
                auraExplosionLoopEffect.setLoop(true)
                this.playEffect("aura-explosion-loop")
              }
            }, buffer.duration * 1000)
          }
        }
      }

      // Try to set up listener immediately, or use fallback
      setTimeout(setupAuraExplosionEndListener, 0)
    }

    // Screen distortion
    if (this.particles) {
      gsap.to(this.distortionProgress, {
        value: 1,
        duration: 4,
        ease: "expo.out",
        onComplete: () => {
          this.distortionProgress.value = 0
        },
      })
      this.particles.settings.animationType.value = 1
      this.man.outerParticles.settings.speed.value = 2
      this.camera.alreadyShaken = true
    }
  }

  setPPDebug({
    bloomPass,
    outlinePass,
  }: {
    bloomPass: BloomNode
    outlinePass: ReturnType<typeof outline>
  }) {
    if (!this.debugFolder) {
      return
    }

    const pPFolder = this.debugFolder!.addFolder({
      title: "💅 Post Processing",
      expanded: false,
    })

    pPFolder.addBinding(bloomPass.strength, "value", {
      label: "Bloom strength",
      min: 0,
      max: 0.5,
      step: 0.001,
    })

    pPFolder.addBinding(bloomPass.radius, "value", {
      label: "Bloom radius",
      min: 0,
      max: 0.5,
      step: 0.001,
    })

    pPFolder.addBinding(bloomPass.threshold, "value", {
      label: "Bloom threshold",
      min: 0,
      max: 0.5,
      step: 0.001,
    })

    pPFolder.addBinding(this.blurDirectionNode, "value", {
      label: "Blur Direction",
      min: 0,
      max: 10,
      step: 0.1,
    })

    pPFolder.addBinding(this.distortionScale, "value", {
      label: "Distortion Scale",
      min: -10,
      max: 10,
      step: 0.01,
    })

    pPFolder.addBinding(this.distortionProgress, "value", {
      label: "Distortion Progress",
      min: 0,
      max: 1,
      step: 0.01,
    })

    // Outline pass
    const outlineFolder = pPFolder.addFolder({
      title: "📐 Outline",
      expanded: false,
    })

    outlineFolder.addBinding(this.edgeStrength, "value", {
      label: "Edge strength",
      min: 0,
      max: 10,
      step: 0.1,
    })

    outlineFolder.addBinding(this.edgeGlow, "value", {
      label: "Edge glow",
      min: 0,
      max: 2,
      step: 0.01,
    })

    outlineFolder.addBinding(this.edgeThickness, "value", {
      label: "Edge thickness",
      min: 0.1,
      max: 8,
      step: 0.1,
    })

    outlineFolder.addBinding(this.pulsePeriod, "value", {
      label: "Pulse period",
      min: 0.1,
      max: 5,
      step: 0.1,
    })

    const outlineColorSettings = {
      visibleEdgeColor: "#" + this.visibleEdgeColor.value.getHexString(),
    }

    outlineFolder
      .addBinding(outlineColorSettings, "visibleEdgeColor", {
        label: "Visible edge color",
        view: "color",
      })
      .on("change", (ev) => {
        this.visibleEdgeColor.value.set(ev.value)
      })

    const outlineDownSample = { ratio: outlinePass.downSampleRatio }
    outlineFolder
      .addBinding(outlineDownSample, "ratio", {
        label: "Down sample ratio",
        min: 1,
        max: 4,
        step: 1,
      })
      .on("change", (ev) => {
        outlinePass.downSampleRatio = ev.value
      })
  }

  setFogDebug() {
    if (!this.debugFolder || !this.fog) {
      return
    }

    const fogFolder = this.debugFolder.addFolder({
      title: "🌫️ Fog",
      expanded: false,
    })

    // Convert fog color to hex string for the color picker
    const fogColorHex = "#" + this.fog.color.getHexString()

    const fogSettings = {
      color: fogColorHex,
      near: this.fog.near,
      far: this.fog.far,
    }

    fogFolder
      .addBinding(fogSettings, "color", {
        view: "color",
      })
      .on("change", (ev) => {
        this.fog.color.set(ev.value)
      })

    fogFolder
      .addBinding(fogSettings, "near", {
        min: 0,
        max: 10,
        step: 1,
      })
      .on("change", (ev) => {
        this.fog.near = ev.value
      })

    fogFolder
      .addBinding(fogSettings, "far", {
        min: 0,
        max: 30,
        step: 1,
      })
      .on("change", (ev) => {
        this.fog.far = ev.value
      })
  }

  update(params: { delta: number; elapsed: number }) {
    super.update(params)

    // this.experience.renderer.instance.setRenderTarget(this.textScene.fbo)
    // this.textScene.update(params)
    if (this.projectList) {
      this.projectList.update()
    }

    if (this.particles) {
      this.particles.update()
    }

    if (this.man) {
      this.man.update(params)
      this.visibleEdgeColor.value.copy(this.man.settings.circleMask.color.value)
    }

    // if (this.glow) {
    //   this.glow.update(params)
    // }

    this.camera.update()

    this.postProcessing.render()

    this.updateCloseTextPosition()
  }

  private updateCloseTextPosition() {
    if (!this.closeText?.textMesh?.visible) {
      return
    }

    const pointer = this.experience.pointer
    if (pointer.primaryInput !== "mouse") {
      this.closeText.leave()
      this.hasSetCloseTextPosition = false
      return
    }
    const { width, height } = this.sizes

    // `TextScene` is set up so that at z=0, world units map to screen pixels.
    // Convert pointer pixels (top-left origin) to centered world coords (y-up).
    const targetX = pointer.x - width / 2 + 18
    const targetY = height / 2 - pointer.y - 18

    if (!this.hasSetCloseTextPosition) {
      this.hasSetCloseTextPosition = true
      this.closeText.textMesh.position.x = targetX
      this.closeText.textMesh.position.y = targetY
      return
    }

    const alpha = 0.12
    this.closeText.textMesh.position.x = lerp(
      this.closeText.textMesh.position.x,
      targetX,
      alpha,
    )
    this.closeText.textMesh.position.y = lerp(
      this.closeText.textMesh.position.y,
      targetY,
      alpha,
    )
  }

  resize() {
    super.resize()

    this.setIsMobileUniform()

    if (this.projectList) {
      this.projectList.resize()
    }
  }
}
